import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { expensesAPI, venuesAPI, accountsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useOffline } from '../context/OfflineContext';
import usePermissions from '../hooks/usePermissions';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { 
  Plus, Search, Receipt, Edit, Trash2, Filter, 
  Building2, Calendar, CreditCard, Wallet, RefreshCw,
  TrendingUp, TrendingDown, PieChart, BarChart3,
  Zap, Wrench, Users, Package, Megaphone, Home,
  Shield, PenTool as Tool, Utensils, Truck, FileText, MoreHorizontal,
  IndianRupee, Download, ChevronDown, Settings, Tag, Lock,
  Landmark, ArrowUpCircle, ArrowDownCircle, DollarSign, Banknote,
  ChevronRight, X, ArrowLeftRight
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Wallet },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
  { value: 'upi', label: 'UPI', icon: CreditCard },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'cheque', label: 'Cheque', icon: FileText }
];

const INCOME_SOURCES = [
  { value: 'manual', label: 'Manual Entry' },
  { value: 'booking_payment', label: 'Booking Payment' },
  { value: 'refund', label: 'Refund' },
  { value: 'other', label: 'Other' }
];

const CATEGORY_ICONS = {
  zap: Zap,
  wrench: Wrench,
  users: Users,
  package: Package,
  megaphone: Megaphone,
  home: Home,
  shield: Shield,
  tool: Tool,
  utensils: Utensils,
  truck: Truck,
  'file-text': FileText,
  'more-horizontal': MoreHorizontal,
  receipt: Receipt,
  wallet: Wallet,
  landmark: Landmark,
  banknote: Banknote
};

const ACCOUNT_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const Accounts = () => {
  const [activeTab, setActiveTab] = useState('income');
  
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  
  // Income state
  const [income, setIncome] = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [accountsSummary, setAccountsSummary] = useState(null);
  
  // Loading & Dialog state
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Edit state
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingIncome, setEditingIncome] = useState(null);
  
  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  
  // Reports state
  const [showReports, setShowReports] = useState(false);
  const [reportStartDate, setReportStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportTransactionType, setReportTransactionType] = useState('all'); // all, income, expense

  // Account head transactions state
  const [selectedAccountHead, setSelectedAccountHead] = useState(null);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [accountTxnSummary, setAccountTxnSummary] = useState(null);
  const [accountTxnLoading, setAccountTxnLoading] = useState(false);
  const [accountTxnDialogOpen, setAccountTxnDialogOpen] = useState(false);
  const [accountTxnFilter, setAccountTxnFilter] = useState('all');
  
  // Transfers (Contra) state
  const [transfers, setTransfers] = useState([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: '',
    transferDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Filters
  const [filterVenue, setFilterVenue] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterAccount, setFilterAccount] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form data
  const [expenseForm, setExpenseForm] = useState({
    venueId: '',
    categoryId: '',
    accountHeadId: '',
    amount: '',
    description: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    vendorName: '',
    invoiceNumber: '',
    notes: ''
  });
  
  const [incomeForm, setIncomeForm] = useState({
    accountHeadId: '',
    amount: '',
    description: '',
    incomeDate: new Date().toISOString().split('T')[0],
    source: 'manual',
    venueId: '',
    notes: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#6B7280'
  });
  
  const [accountForm, setAccountForm] = useState({
    name: '',
    description: '',
    initialBalance: '',
    color: '#3B82F6'
  });
  
  const { toast } = useToast();
  const { isOffline, checkOfflineAction } = useOffline();
  const { 
    canViewExpenses, canCreateExpenses, canEditExpenses, canDeleteExpenses, canExportExpenses,
    canViewAccounts, canCreateAccounts, canEditAccounts, canDeleteAccounts, canExportAccounts,
    isAdmin
  } = usePermissions();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Auto-recalculate all balances on page load to ensure accuracy
      await accountsAPI.recalculateAllBalances().catch(() => {});
      
      const [expensesData, categoriesData, venuesData, accountHeadsData, accountsSummaryData] = await Promise.all([
        expensesAPI.getAll(),
        expensesAPI.getCategories(),
        venuesAPI.getAll(),
        accountsAPI.getAccountHeads(),
        accountsAPI.getSummary()
      ]);
      
      setExpenses(expensesData || []);
      setCategories(categoriesData || []);
      setVenues(venuesData || []);
      setAccountHeads(accountHeadsData || []);
      setAccountsSummary(accountsSummaryData || null);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load accounts data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadIncome = useCallback(async () => {
    try {
      const incomeData = await accountsAPI.getIncome();
      setIncome(incomeData || []);
    } catch (error) {
      console.error('Failed to load income:', error);
    }
  }, []);

  const loadTransfers = useCallback(async () => {
    try {
      const transfersData = await accountsAPI.getTransfers();
      setTransfers(transfersData || []);
    } catch (error) {
      console.error('Failed to load transfers:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'income') {
      loadIncome();
    }
    if (activeTab === 'transfers') {
      loadTransfers();
    }
    // Refresh account heads data when viewing that tab
    if (activeTab === 'accounts') {
      loadData();
    }
  }, [activeTab, loadIncome, loadTransfers, loadData]);

  // ============= EXPENSE HANDLERS =============
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction()) return;
    
    try {
      const data = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        accountHeadId: expenseForm.accountHeadId || null
      };
      
      if (editingExpense) {
        await expensesAPI.update(editingExpense.id, data);
        toast({ title: 'Success', description: 'Expense updated successfully' });
      } else {
        await expensesAPI.create(data);
        toast({ title: 'Success', description: 'Expense added successfully' });
      }
      
      setDialogOpen(false);
      setEditingExpense(null);
      resetExpenseForm();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save expense', variant: 'destructive' });
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      venueId: expense.venueId,
      categoryId: expense.categoryId,
      accountHeadId: expense.accountHeadId || '',
      amount: expense.amount.toString(),
      description: expense.description,
      expenseDate: expense.expenseDate.split('T')[0],
      paymentMethod: expense.paymentMethod,
      vendorName: expense.vendorName || '',
      invoiceNumber: expense.invoiceNumber || '',
      notes: expense.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteExpense = async () => {
    if (!checkOfflineAction()) return;
    
    try {
      await expensesAPI.delete(itemToDelete.id);
      toast({ title: 'Success', description: 'Expense deleted successfully' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete expense', variant: 'destructive' });
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      venueId: '',
      categoryId: '',
      accountHeadId: '',
      amount: '',
      description: '',
      expenseDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      vendorName: '',
      invoiceNumber: '',
      notes: ''
    });
  };

  // ============= INCOME HANDLERS =============
  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction()) return;
    
    try {
      const data = {
        accountHeadId: incomeForm.accountHeadId,
        amount: parseFloat(incomeForm.amount),
        description: incomeForm.description,
        incomeDate: incomeForm.incomeDate,
        source: incomeForm.source,
        venueId: incomeForm.venueId || null,
        notes: incomeForm.notes || ''
      };
      
      
      if (editingIncome) {
        const result = await accountsAPI.updateIncome(editingIncome.id, data);
        toast({ title: 'Success', description: 'Income updated successfully' });
      } else {
        await accountsAPI.createIncome(data);
        toast({ title: 'Success', description: 'Income added successfully' });
      }
      
      setIncomeDialogOpen(false);
      setEditingIncome(null);
      resetIncomeForm();
      loadIncome();
      loadData();
    } catch (error) {
      console.error('Income submit error:', error);
      toast({ title: 'Error', description: error.message || 'Failed to save income', variant: 'destructive' });
    }
  };

  const handleEditIncome = (inc) => {
    setEditingIncome(inc);
    setIncomeForm({
      accountHeadId: inc.accountHeadId,
      amount: inc.amount.toString(),
      description: inc.description,
      incomeDate: inc.incomeDate.split('T')[0],
      source: inc.source || 'manual',
      venueId: inc.venueId || '',
      notes: inc.notes || ''
    });
    setIncomeDialogOpen(true);
  };

  const handleDeleteIncome = async () => {
    if (!checkOfflineAction()) return;
    
    try {
      await accountsAPI.deleteIncome(itemToDelete.id);
      toast({ title: 'Success', description: 'Income deleted successfully' });
      loadIncome();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete income', variant: 'destructive' });
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const resetIncomeForm = () => {
    setIncomeForm({
      accountHeadId: '',
      amount: '',
      description: '',
      incomeDate: new Date().toISOString().split('T')[0],
      source: 'manual',
      venueId: '',
      notes: ''
    });
  };

  // ============= ACCOUNT HEAD HANDLERS =============
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction()) return;
    
    try {
      const data = {
        ...accountForm,
        initialBalance: parseFloat(accountForm.initialBalance) || 0
      };
      
      if (editingAccount) {
        await accountsAPI.updateAccountHead(editingAccount.id, data);
        toast({ title: 'Success', description: 'Account updated successfully' });
      } else {
        await accountsAPI.createAccountHead(data);
        toast({ title: 'Success', description: 'Account created successfully' });
      }
      
      setAccountDialogOpen(false);
      setEditingAccount(null);
      resetAccountForm();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save account', variant: 'destructive' });
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name,
      description: account.description || '',
      initialBalance: account.initialBalance?.toString() || '0',
      color: account.color || '#3B82F6'
    });
    setAccountDialogOpen(true);
  };

  const handleDeleteAccount = async () => {
    if (!checkOfflineAction()) return;
    
    try {
      await accountsAPI.deleteAccountHead(itemToDelete.id);
      toast({ title: 'Success', description: 'Account deleted successfully' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete account', variant: 'destructive' });
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const resetAccountForm = () => {
    setAccountForm({
      name: '',
      description: '',
      initialBalance: '',
      color: '#3B82F6'
    });
  };

  // ============= ACCOUNT HEAD TRANSACTIONS =============
  const handleAccountHeadClick = async (account) => {
    setSelectedAccountHead(account);
    setAccountTxnDialogOpen(true);
    setAccountTxnLoading(true);
    setAccountTxnFilter('all');
    try {
      const data = await accountsAPI.getAccountTransactions(account.id);
      setAccountTransactions(data.transactions || []);
      setAccountTxnSummary(data.summary || null);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load transactions', variant: 'destructive' });
      setAccountTransactions([]);
    } finally {
      setAccountTxnLoading(false);
    }
  };

  const filteredAccountTransactions = accountTransactions.filter(t => {
    if (accountTxnFilter === 'all') return true;
    if (accountTxnFilter === 'income') return t.type === 'income';
    if (accountTxnFilter === 'expense') return t.type === 'expense';
    if (accountTxnFilter === 'transfer') return t.type === 'transfer_in' || t.type === 'transfer_out';
    return true;
  });

  // ============= CATEGORY HANDLERS =============
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction()) return;
    
    try {
      if (editingCategory) {
        await expensesAPI.updateCategory(editingCategory.id, categoryForm);
        toast({ title: 'Success', description: 'Category updated successfully' });
      } else {
        await expensesAPI.createCategory(categoryForm);
        toast({ title: 'Success', description: 'Category created successfully' });
      }
      
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', color: '#6B7280' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save category', variant: 'destructive' });
    }
  };

  // ============= TRANSFER HANDLERS =============
  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction()) return;
    
    try {
      if (!transferForm.fromAccountId || !transferForm.toAccountId || !transferForm.amount) {
        toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
        return;
      }
      
      await accountsAPI.createTransfer({
        ...transferForm,
        amount: parseFloat(transferForm.amount)
      });
      
      toast({ title: 'Success', description: 'Transfer completed successfully' });
      setTransferDialogOpen(false);
      setTransferForm({
        fromAccountId: '',
        toAccountId: '',
        amount: '',
        description: '',
        transferDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      loadTransfers();
      loadData(); // Refresh account balances
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to create transfer', variant: 'destructive' });
    }
  };

  const handleDeleteTransfer = async (transferId) => {
    if (!checkOfflineAction()) return;
    
    try {
      await accountsAPI.deleteTransfer(transferId);
      toast({ title: 'Success', description: 'Transfer reversed successfully' });
      loadTransfers();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to reverse transfer', variant: 'destructive' });
    }
  };

  // ============= REPORT HANDLERS =============
  const loadReport = async () => {
    setReportLoading(true);
    try {
      const data = await accountsAPI.getReport(reportStartDate, reportEndDate, null, reportTransactionType);
      setReportData(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load report', variant: 'destructive' });
    } finally {
      setReportLoading(false);
    }
  };

  // Format currency for PDF (without rupee symbol which doesn't render well)
  const formatCurrencyForPDF = (amount) => {
    const num = Number(amount) || 0;
    return 'Rs. ' + num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Download Report as PDF
  const downloadReportPDF = () => {
    if (!reportData) {
      toast({ title: 'Error', description: 'Please generate a report first', variant: 'destructive' });
      return;
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.text('Accounts Report', pageWidth / 2, 20, { align: 'center' });
    
    // Period
    doc.setFontSize(12);
    doc.text(`Period: ${formatDate(reportStartDate)} - ${formatDate(reportEndDate)}`, pageWidth / 2, 30, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(14);
    doc.text('Summary', 14, 45);
    
    const summaryData = [
      ['Total Income', formatCurrencyForPDF(reportData.summary?.totalIncome || 0)],
      ['Total Expenses', formatCurrencyForPDF(reportData.summary?.totalExpenses || 0)],
      ['Net Flow', formatCurrencyForPDF(reportData.summary?.netFlow || 0)]
    ];
    
    autoTable(doc, {
      startY: 50,
      head: [['Metric', 'Amount']],
      body: summaryData,
      theme: 'striped'
    });
    
    // Transactions Section
    if (reportData.transactions && reportData.transactions.length > 0) {
      const lastY = doc.lastAutoTable.finalY || 80;
      doc.setFontSize(14);
      doc.text('Transactions', 14, lastY + 15);
      
      const transactionData = reportData.transactions.map((t, index) => [
        index + 1,
        formatDate(t.date),
        t.type === 'income' ? 'Income' : 'Expense',
        t.description || '-',
        t.accountHeadName || '-',
        formatCurrencyForPDF(t.amount)
      ]);
      
      autoTable(doc, {
        startY: lastY + 20,
        head: [['#', 'Date', 'Type', 'Description', 'Account Head', 'Amount']],
        body: transactionData,
        theme: 'striped',
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 60 },
          4: { cellWidth: 35 },
          5: { cellWidth: 30 }
        }
      });
    }
    
    doc.save(`accounts_report_${reportStartDate}_${reportEndDate}.pdf`);
    toast({ title: 'Success', description: 'PDF report downloaded successfully' });
  };

  // Download Report as Excel
  const downloadReportExcel = () => {
    if (!reportData) {
      toast({ title: 'Error', description: 'Please generate a report first', variant: 'destructive' });
      return;
    }
    
    // Summary sheet data
    const summaryData = [
      ['Accounts Report'],
      [`Period: ${formatDate(reportStartDate)} - ${formatDate(reportEndDate)}`],
      [],
      ['Summary'],
      ['Metric', 'Amount'],
      ['Total Income', reportData.summary?.totalIncome || 0],
      ['Total Expenses', reportData.summary?.totalExpenses || 0],
      ['Net Flow', reportData.summary?.netFlow || 0]
    ];
    
    // Transactions data
    const transactionsData = [
      [],
      ['Transactions'],
      ['#', 'Date', 'Type', 'Description', 'Account Head', 'Amount']
    ];
    
    if (reportData.transactions && reportData.transactions.length > 0) {
      reportData.transactions.forEach((t, index) => {
        transactionsData.push([
          index + 1,
          formatDate(t.date),
          t.type === 'income' ? 'Income' : 'Expense',
          t.description || '-',
          t.accountHeadName || '-',
          t.amount || 0
        ]);
      });
    }
    
    // Combine data
    const allData = [...summaryData, ...transactionsData];
    
    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(allData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Accounts Report');
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 },
      { wch: 15 },
      { wch: 10 },
      { wch: 40 },
      { wch: 20 },
      { wch: 15 }
    ];
    
    // Download
    XLSX.writeFile(workbook, `accounts_report_${reportStartDate}_${reportEndDate}.xlsx`);
    toast({ title: 'Success', description: 'Excel report downloaded successfully' });
  };

  // ============= FILTER HELPERS =============
  const filteredExpenses = expenses.filter(exp => {
    if (filterVenue !== 'all' && exp.venueId !== filterVenue) return false;
    if (filterCategory !== 'all' && exp.categoryId !== filterCategory) return false;
    if (filterPayment !== 'all' && exp.paymentMethod !== filterPayment) return false;
    if (filterAccount !== 'all' && exp.accountHeadId !== filterAccount) return false;
    if (searchTerm && !exp.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const filteredIncome = income.filter(inc => {
    if (filterAccount !== 'all' && inc.accountHeadId !== filterAccount) return false;
    if (filterVenue !== 'all' && inc.venueId !== filterVenue) return false;
    if (searchTerm && !inc.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getAccountName = (accountId) => {
    const account = accountHeads.find(a => a.id === accountId);
    return account?.name || 'Others';
  };

  const getCategoryIcon = (iconName) => {
    const Icon = CATEGORY_ICONS[iconName] || Receipt;
    return Icon;
  };

  const hasActiveFilters = filterVenue !== 'all' || filterCategory !== 'all' || filterPayment !== 'all' || filterAccount !== 'all';

  // Permission checks
  const hasAccountsAccess = canViewAccounts() || canViewExpenses();

  if (!hasAccountsAccess) {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <Card className="p-4 sm:p-6 text-center w-full max-w-sm">
          <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 dark:text-muted-foreground mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-foreground">Access Restricted</h3>
          <p className="text-sm text-gray-500 dark:text-muted-foreground">You don&apos;t have permission to access the Accounts module.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4 w-full max-w-full overflow-hidden" data-testid="accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-foreground flex items-center gap-1.5">
          <Landmark className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 flex-shrink-0" />
          Accounts
        </h1>
        
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          disabled={loading}
          className="flex-shrink-0 h-9 sm:h-10 px-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary Cards */}
      {accountsSummary && (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 [&>*]:min-w-0">
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-teal-200 dark:border-teal-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-1 text-teal-600 mb-0.5">
                <Wallet className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium">Balance</span>
              </div>
              <p className="text-sm sm:text-base font-bold text-teal-700 dark:text-teal-400 truncate">
                {formatCurrency(accountsSummary.totalBalance)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-1 text-green-600 mb-0.5">
                <ArrowUpCircle className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium">Income</span>
              </div>
              <p className="text-sm sm:text-base font-bold text-green-700 dark:text-green-400 truncate">
                {formatCurrency(accountsSummary.totalIncome)}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-1 text-red-600 mb-0.5">
                <ArrowDownCircle className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium">Expenses</span>
              </div>
              <p className="text-sm sm:text-base font-bold text-red-700 dark:text-red-400 truncate">
                {formatCurrency(accountsSummary.totalExpenses)}
              </p>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${accountsSummary.netFlow >= 0 ? 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800' : 'from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800'}`}>
            <CardContent className="p-3 sm:p-4">
              <div className={`flex items-center gap-1 ${accountsSummary.netFlow >= 0 ? 'text-blue-600' : 'text-orange-600'} mb-0.5`}>
                <TrendingUp className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium">Net</span>
              </div>
              <p className={`text-sm sm:text-base font-bold ${accountsSummary.netFlow >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-orange-700 dark:text-orange-400'} truncate`}>
                {formatCurrency(accountsSummary.netFlow)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs Card */}
      <Card className="border-0 shadow-md">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0" defaultValue="income">
          {/* Tab Headers - Reordered: Income → Expenses → Heads → Transfers → Reports */}
          <CardHeader className="border-b dark:border-border bg-gradient-to-r from-slate-50 to-gray-50 dark:from-secondary dark:to-muted p-0">
            <div className="overflow-x-auto">
              <TabsList className="inline-flex w-auto min-w-full justify-start rounded-none border-0 bg-transparent p-0 h-auto">
                <TabsTrigger
                  value="income"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 data-[state=active]:bg-transparent px-2 sm:px-4 py-2 text-sm sm:text-sm whitespace-nowrap"
                  disabled={!canViewAccounts()}
                >
                  <ArrowUpCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-green-500" />
                  Income
                </TabsTrigger>
                <TabsTrigger
                  value="expenses"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent px-2 sm:px-4 py-2 text-sm sm:text-sm whitespace-nowrap"
                >
                  <ArrowDownCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-500" />
                  Expenses
                </TabsTrigger>
                <TabsTrigger
                  value="accounts"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent px-2 sm:px-4 py-2 text-sm sm:text-sm whitespace-nowrap"
                  disabled={!canViewAccounts()}
                >
                  <Landmark className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-blue-500" />
                  Heads
                </TabsTrigger>
                <TabsTrigger
                  value="transfers"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent px-2 sm:px-4 py-2 text-sm sm:text-sm whitespace-nowrap"
                  disabled={!canEditAccounts()}
                >
                  <ArrowLeftRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-orange-500" />
                  Transfer
                </TabsTrigger>
                {isAdmin() && (
                  <TabsTrigger
                    value="reports"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-500 data-[state=active]:bg-transparent px-2 sm:px-4 py-2 text-sm sm:text-sm whitespace-nowrap"
                  >
                    <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-purple-500" />
                    Reports
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
          </CardHeader>

          {/* INCOME TAB - Card Layout */}
          <TabsContent value="income" className="p-2 sm:p-3 m-0">
            {/* Search & Add */}
            <div className="flex gap-1.5 mb-2">
              <div className="flex-1 relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 h-9 text-sm"
                />
              </div>
              <Select value={filterAccount} onValueChange={setFilterAccount}>
                <SelectTrigger className="w-[60px] h-9 text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {accountHeads.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {canCreateAccounts() && (
                <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" aria-label="Add income" title="Add income" data-testid="add-income-btn" className="bg-green-600 hover:bg-green-700 h-9 px-2" onClick={() => { setEditingIncome(null); resetIncomeForm(); }}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-sm p-3">
                    <DialogHeader><DialogTitle className="text-sm">{editingIncome ? 'Edit' : 'Add'} Income</DialogTitle></DialogHeader>
                    <form onSubmit={handleIncomeSubmit} className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm">Account *</Label>
                          <Select value={incomeForm.accountHeadId} onValueChange={(v) => setIncomeForm({...incomeForm, accountHeadId: v})}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{accountHeads.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Source</Label>
                          <Select value={incomeForm.source} onValueChange={(v) => setIncomeForm({...incomeForm, source: v})}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{INCOME_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm">Amount *</Label>
                          <Input type="number" value={incomeForm.amount} onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})} className="h-9 text-sm" required />
                        </div>
                        <div>
                          <Label className="text-sm">Date *</Label>
                          <Input type="date" value={incomeForm.incomeDate} onChange={(e) => setIncomeForm({...incomeForm, incomeDate: e.target.value})} className="h-9 text-sm" required />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Description</Label>
                        <Input value={incomeForm.description} onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})} className="h-9 text-sm" />
                      </div>
                      <Button type="submit" className="w-full h-9 text-sm bg-green-600">{editingIncome ? 'Update' : 'Add'}</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            
            {/* Income Cards Grid */}
            <div className="grid grid-cols-1 gap-1.5">
              {filteredIncome.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-muted-foreground text-sm py-4">No income records</p>
              ) : (
                filteredIncome.map(inc => (
                  <Card key={inc.id} className="hover:shadow-sm transition-shadow">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start gap-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 mb-0.5">
                            <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 text-xs px-1 py-0">{inc.source || 'manual'}</Badge>
                            <span className="text-xs text-gray-400 dark:text-muted-foreground">{formatDate(inc.incomeDate)}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 dark:text-foreground truncate">{inc.description || 'Income'}</p>
                          <p className="text-xs text-gray-500 dark:text-muted-foreground">{accountHeads.find(a => a.id === inc.accountHeadId)?.name || '-'}</p>
                          {(inc.createdByName || inc.updatedByName) && (
                            <p className="text-xs text-gray-400 dark:text-muted-foreground/80 truncate" data-testid={`income-creator-${inc.id}`}>
                              {inc.updatedByName ? `Edited by ${inc.updatedByName}` : `By ${inc.createdByName}`}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-green-600">{formatCurrency(inc.amount)}</p>
                          <div className="flex gap-1.5 mt-0.5">
                            {canEditAccounts() && (
                              <Button size="sm" variant="ghost" className="h-10 w-8 p-0" onClick={() => handleEditIncome(inc)}><Edit className="w-3.5 h-3.5" /></Button>
                            )}
                            {canDeleteAccounts() && (
                              <Button size="sm" variant="ghost" className="h-10 w-8 p-0 text-red-500" onClick={() => { setItemToDelete(inc); setDeleteType('income'); setDeleteConfirmOpen(true); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* EXPENSES TAB - Card Layout */}
          <TabsContent value="expenses" className="p-2 sm:p-3 m-0">
            {/* Mobile-Optimized Search & Actions */}
            <div className="space-y-2 mb-3">
              {/* Search Row */}
              <div className="flex gap-1.5">
                <div className="flex-1 relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-7 h-10 text-sm"
                  />
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex-shrink-0 h-10 px-2 ${hasActiveFilters ? 'border-teal-500 text-teal-600' : ''}`}
                >
                  <Filter className="w-3.5 h-3.5" />
                </Button>

                {/* Category Management Button */}
                {canEditAccounts() && (
                  <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0 h-10 px-2"
                        onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', description: '', color: '#6B7280' }); }}
                      >
                        <Tag className="w-3.5 h-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-md mx-auto p-3 sm:p-6 max-h-[90vh] overflow-y-auto">
                      <DialogHeader className="pb-2">
                        <DialogTitle className="text-base">{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
                      </DialogHeader>
                      
                      {/* Category Form */}
                      <form onSubmit={handleCategorySubmit} className="space-y-2 mb-3">
                        <div>
                          <Label className="text-sm">Name *</Label>
                          <Input
                            value={categoryForm.name}
                            onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                            placeholder="e.g., Utilities"
                            className="h-10 text-sm"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Description</Label>
                          <Input
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                            placeholder="Optional"
                            className="h-10 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={categoryForm.color}
                              onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                              className="w-10 h-10 p-0.5 cursor-pointer"
                            />
                            <Input
                              value={categoryForm.color}
                              onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                              className="flex-1 h-10 text-sm"
                            />
                          </div>
                        </div>
                        <Button type="submit" className="w-full h-10 text-sm bg-teal-600 hover:bg-teal-700">
                          {editingCategory ? 'Update' : 'Add'}
                        </Button>
                      </form>
                      
                      {/* Existing Categories List */}
                      <div className="border-t pt-2">
                        <h4 className="text-sm font-medium mb-2">Categories ({categories.length})</h4>
                        <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                          {categories.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-muted-foreground text-center py-2">No categories</p>
                          ) : (
                            categories.map(cat => (
                              <div key={cat.id} className="flex items-center justify-between p-1.5 bg-gray-50 dark:bg-secondary rounded text-sm">
                                <div className="flex items-center gap-1.5">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: cat.color || '#6B7280' }}
                                  />
                                  <span className="text-sm font-medium">{cat.name}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-9 w-9 p-0"
                                    onClick={() => {
                                      setEditingCategory(cat);
                                      setCategoryForm({ name: cat.name, description: cat.description || '', color: cat.color || '#6B7280' });
                                    }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-9 w-9 p-0 text-red-500"
                                    onClick={async () => {
                                      if (window.confirm('Delete this category?')) {
                                        try {
                                          await expensesAPI.deleteCategory(cat.id);
                                          toast({ title: 'Success', description: 'Category deleted' });
                                          loadData();
                                        } catch (error) {
                                          toast({ title: 'Error', description: error.message || 'Failed to delete', variant: 'destructive' });
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                {canCreateExpenses() && (
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm"
                        aria-label="Add expense"
                        title="Add expense"
                        data-testid="add-expense-btn"
                        className="bg-teal-600 hover:bg-teal-700 h-9 shrink-0"
                        onClick={() => { setEditingExpense(null); resetExpenseForm(); }}
                      >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline ml-1">Add</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto p-4 sm:p-6">
                      <DialogHeader>
                        <DialogTitle className="text-base sm:text-lg">{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleExpenseSubmit} className="space-y-3 sm:space-y-4">
                        {/* Venue & Category */}
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <Label className="text-sm sm:text-sm">Venue *</Label>
                            <Select value={expenseForm.venueId} onValueChange={(v) => setExpenseForm({...expenseForm, venueId: v})}>
                              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select venue" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="collective">All Venues</SelectItem>
                                {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm sm:text-sm">Category *</Label>
                            <Select value={expenseForm.categoryId} onValueChange={(v) => setExpenseForm({...expenseForm, categoryId: v})}>
                              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                              <SelectContent>
                                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Amount & Date - Stack on mobile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm sm:text-sm">Amount (₹) *</Label>
                            <Input 
                              type="number" 
                              value={expenseForm.amount}
                              onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                              placeholder="0.00"
                              className="h-9 text-sm"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-sm sm:text-sm">Date *</Label>
                            <Input 
                              type="date" 
                              value={expenseForm.expenseDate}
                              onChange={(e) => setExpenseForm({...expenseForm, expenseDate: e.target.value})}
                              className="h-9 text-sm"
                              required
                            />
                          </div>
                        </div>
                        
                        {/* Payment & Account - Stack on mobile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm sm:text-sm">Payment Method</Label>
                            <Select value={expenseForm.paymentMethod} onValueChange={(v) => setExpenseForm({...expenseForm, paymentMethod: v})}>
                              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm sm:text-sm">From Account</Label>
                            <Select value={expenseForm.accountHeadId} onValueChange={(v) => setExpenseForm({...expenseForm, accountHeadId: v})}>
                              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Optional" /></SelectTrigger>
                              <SelectContent>
                                {accountHeads.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Description */}
                        <div>
                          <Label className="text-sm sm:text-sm">Description *</Label>
                          <Input 
                            value={expenseForm.description}
                            onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                            placeholder="Brief description"
                            className="h-9 text-sm"
                            required
                          />
                        </div>
                        
                        {/* Notes - Collapsible on Mobile */}
                        <div>
                          <Label className="text-sm sm:text-sm">Notes (Optional)</Label>
                          <Textarea 
                            value={expenseForm.notes}
                            onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                            placeholder="Additional notes..."
                            rows={2}
                            className="text-sm resize-none"
                          />
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 h-9 text-sm">
                            Cancel
                          </Button>
                          <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 h-9 text-sm">
                            {editingExpense ? 'Update' : 'Add'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Filters - Collapsible on Mobile */}
              {showFilters && (
                <div className="grid grid-cols-2 sm:flex gap-2 p-3 bg-gray-50 dark:bg-secondary rounded-lg">
                  <Select value={filterVenue} onValueChange={setFilterVenue}>
                    <SelectTrigger className="h-10 text-sm sm:w-[130px]">
                      <SelectValue placeholder="Venue" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>
                      {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterAccount} onValueChange={setFilterAccount}>
                    <SelectTrigger className="h-10 text-sm sm:w-[130px]">
                      <SelectValue placeholder="Account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      {accountHeads.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  
                  {hasActiveFilters && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setFilterVenue('all'); setFilterAccount('all'); setFilterCategory('all'); setFilterPayment('all'); }}
                      className="h-10 text-sm col-span-2 sm:col-span-1"
                    >
                      <X className="w-3 h-3 mr-1" /> Clear
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Expenses List - Mobile Optimized */}
            <div className="space-y-2 sm:space-y-3">
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mx-auto text-teal-600" />
                  <p className="text-sm text-gray-500 dark:text-muted-foreground mt-2">Loading...</p>
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                  <p className="text-sm text-gray-500 dark:text-muted-foreground">No expenses found</p>
                </div>
              ) : (
                filteredExpenses.map((expense) => {
                  const CategoryIcon = getCategoryIcon(categories.find(c => c.id === expense.categoryId)?.icon);
                  return (
                    <div 
                      key={expense.id}
                      className="p-3 sm:p-4 border rounded-lg bg-white dark:bg-card hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-2.5 sm:gap-3">
                        {/* Icon */}
                        <div 
                          className="p-1.5 sm:p-2 rounded-lg shrink-0"
                          style={{ backgroundColor: `${expense.categoryColor}20` }}
                        >
                          <CategoryIcon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: expense.categoryColor }} />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-foreground truncate">{expense.description}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                <Badge variant="outline" className="text-sm sm:text-sm px-1.5 py-0">{expense.categoryName}</Badge>
                                {expense.accountHeadId && (
                                  <Badge variant="secondary" className="text-sm sm:text-sm px-1.5 py-0">
                                    {getAccountName(expense.accountHeadId)}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm sm:text-sm text-gray-500 dark:text-muted-foreground mt-1">
                                {formatDate(expense.expenseDate)} • {expense.venueName}
                              </p>
                              {(expense.createdByName || expense.updatedByName) && (
                                <p className="text-sm sm:text-sm text-gray-400 dark:text-muted-foreground/80 mt-0.5 truncate" data-testid={`expense-creator-${expense.id}`}>
                                  {expense.updatedByName ? `Edited by ${expense.updatedByName}` : `By ${expense.createdByName}`}
                                </p>
                              )}
                            </div>
                            
                            {/* Amount & Actions */}
                            <div className="text-right shrink-0">
                              <p className="font-bold text-red-600 text-sm sm:text-base">{formatCurrency(expense.amount)}</p>
                              <div className="flex gap-1.5 mt-1 justify-end">
                                {canEditExpenses() && (
                                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => handleEditExpense(expense)}>
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {canDeleteExpenses() && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-9 w-9 p-0 text-red-500"
                                    onClick={() => { setItemToDelete(expense); setDeleteType('expense'); setDeleteConfirmOpen(true); }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* ACCOUNT HEADS TAB - Card Layout */}
          <TabsContent value="accounts" className="p-2 sm:p-3 m-0">
            <div className="flex justify-between items-center mb-2 gap-1">
              <span className="text-sm sm:text-sm text-gray-500 dark:text-muted-foreground">Account Heads</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-6 px-1.5 text-xs" onClick={async () => {
                  try {
                    await accountsAPI.recalculateAllBalances();
                    toast({ title: 'Recalculated' });
                    loadData();
                  } catch (e) {
                    toast({ title: 'Error', variant: 'destructive' });
                  }
                }}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
                {canCreateAccounts() && (
                  <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" aria-label="Add account head" title="Add account head" data-testid="add-account-head-btn" className="bg-blue-600 h-9 px-2 text-sm" onClick={() => { setEditingAccount(null); resetAccountForm(); }}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-xs p-3">
                      <DialogHeader><DialogTitle className="text-sm">{editingAccount ? 'Edit' : 'New'} Account</DialogTitle></DialogHeader>
                      <form onSubmit={handleAccountSubmit} className="space-y-2">
                        <div>
                          <Label className="text-sm">Name *</Label>
                          <Input value={accountForm.name} onChange={(e) => setAccountForm({...accountForm, name: e.target.value})} className="h-9 text-sm" required />
                        </div>
                        <div>
                          <Label className="text-sm">Description</Label>
                          <Input value={accountForm.description} onChange={(e) => setAccountForm({...accountForm, description: e.target.value})} className="h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-sm">Initial Balance</Label>
                          <Input type="number" value={accountForm.initialBalance} onChange={(e) => setAccountForm({...accountForm, initialBalance: e.target.value})} className="h-9 text-sm" />
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {ACCOUNT_COLORS.map(c => (
                            <button key={c} type="button" className={`w-5 h-5 rounded-full border-2 ${accountForm.color === c ? 'border-gray-800' : 'border-transparent'}`} style={{backgroundColor: c}} onClick={() => setAccountForm({...accountForm, color: c})} />
                          ))}
                        </div>
                        <Button type="submit" className="w-full h-9 text-sm bg-blue-600">{editingAccount ? 'Update' : 'Create'}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
            
            {/* Account Cards Grid */}
            <div className="grid grid-cols-2 gap-1.5">
              {accountHeads.map(acc => (
                <Card 
                  key={acc.id} 
                  className="hover:shadow-md cursor-pointer transition-shadow active:scale-[0.98]" 
                  style={{borderLeftColor: acc.color, borderLeftWidth: '3px'}}
                  onClick={() => handleAccountHeadClick(acc)}
                  data-testid={`account-head-card-${acc.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{acc.name}</p>
                        {acc.isDefault && <Badge className="text-[11px] px-1 py-0 bg-gray-100 dark:bg-secondary text-gray-600 dark:text-muted-foreground">Default</Badge>}
                      </div>
                      {canEditAccounts() && (
                        <Button size="sm" variant="ghost" className="h-4 w-4 p-0" onClick={(e) => { e.stopPropagation(); handleEditAccount(acc); }}><Edit className="w-2 h-2" /></Button>
                      )}
                    </div>
                    <p className="text-sm font-bold mt-1" style={{color: acc.color}}>{formatCurrency(acc.currentBalance)}</p>
                    <p className="text-xs text-gray-400 dark:text-muted-foreground mt-0.5 flex items-center gap-1.5"><ChevronRight className="w-2 h-2"/>Tap to view transactions</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* TRANSFERS TAB - Card Layout */}
          <TabsContent value="transfers" className="p-2 sm:p-3 m-0">
            <div className="flex justify-between items-center mb-2 gap-1">
              <span className="text-sm sm:text-sm text-gray-500 dark:text-muted-foreground">Internal Transfers</span>
              <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" aria-label="Add transfer" title="Add transfer" data-testid="add-transfer-btn" className="bg-orange-600 h-9 px-2 text-sm">
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-0.5" />New
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[95vw] max-w-xs p-3">
                  <DialogHeader><DialogTitle className="text-sm">New Transfer</DialogTitle></DialogHeader>
                  <form onSubmit={handleTransferSubmit} className="space-y-2">
                    <div>
                      <Label className="text-sm">From *</Label>
                      <Select value={transferForm.fromAccountId} onValueChange={(v) => setTransferForm({...transferForm, fromAccountId: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{accountHeads.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">To *</Label>
                      <Select value={transferForm.toAccountId} onValueChange={(v) => setTransferForm({...transferForm, toAccountId: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{accountHeads.filter(a => a.id !== transferForm.fromAccountId).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-sm">Amount *</Label>
                        <Input type="number" value={transferForm.amount} onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})} className="h-9 text-sm" required />
                      </div>
                      <div>
                        <Label className="text-sm">Date *</Label>
                        <Input type="date" value={transferForm.transferDate} onChange={(e) => setTransferForm({...transferForm, transferDate: e.target.value})} className="h-9 text-sm" required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Notes</Label>
                      <Input value={transferForm.description} onChange={(e) => setTransferForm({...transferForm, description: e.target.value})} className="h-9 text-sm" />
                    </div>
                    <Button type="submit" className="w-full h-9 text-sm bg-orange-600">Transfer</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Transfer Cards */}
            <div className="grid grid-cols-1 gap-1.5">
              {transfers.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-muted-foreground text-sm py-4">No transfers</p>
              ) : (
                transfers.map(t => (
                  <Card key={t.id} className="hover:shadow-sm">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-center gap-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 text-sm">
                            <span className="text-red-600 font-medium truncate max-w-[60px]">{t.fromAccountName}</span>
                            <ArrowLeftRight className="w-3.5 h-3.5 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
                            <span className="text-green-600 font-medium truncate max-w-[60px]">{t.toAccountName}</span>
                          </div>
                          <p className="text-xs text-gray-400 dark:text-muted-foreground">{formatDate(t.transferDate)}</p>
                          {t.createdByName && (
                            <p className="text-xs text-gray-400 dark:text-muted-foreground/80 truncate" data-testid={`transfer-creator-${t.id}`}>By {t.createdByName}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-sm font-bold">{formatCurrency(t.amount)}</span>
                          {canDeleteAccounts() && (
                            <Button size="sm" variant="ghost" className="h-10 w-8 p-0 text-red-500" onClick={() => { if(window.confirm('Reverse?')) handleDeleteTransfer(t.id); }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* REPORTS TAB - Admin Only */}
          {isAdmin() && (
          <TabsContent value="reports" className="p-3 sm:p-4 m-0">
            {/* Date Range & Controls */}
            <div className="space-y-2 mb-3">
              {/* Date inputs - side by side */}
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-500 dark:text-muted-foreground mb-0.5 block">From</label>
                  <Input type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)} className="h-10 text-sm px-1 w-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-500 dark:text-muted-foreground mb-0.5 block">To</label>
                  <Input type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)} className="h-10 text-sm px-1 w-full" />
                </div>
              </div>
              {/* Type filter */}
              <div>
                <label className="text-xs text-gray-500 dark:text-muted-foreground mb-0.5 block">Type</label>
                <Select value={reportTransactionType} onValueChange={setReportTransactionType}>
                  <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Transactions</SelectItem>
                    <SelectItem value="income">Income Only</SelectItem>
                    <SelectItem value="expense">Expense Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Generate button */}
              <Button onClick={loadReport} disabled={reportLoading} className="h-9 text-sm bg-purple-600 w-full">
                {reportLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Loading...
                  </>
                ) : 'Generate Report'}
              </Button>
              
              {/* Download Buttons */}
              {reportData && (
                <div className="flex gap-2">
                  <Button 
                    onClick={downloadReportPDF} 
                    variant="outline" 
                    size="sm"
                    className="flex-1 h-10 text-sm border-red-300 text-red-600 hover:bg-red-50 dark:bg-red-900/20"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    PDF
                  </Button>
                  <Button 
                    onClick={downloadReportExcel} 
                    variant="outline" 
                    size="sm"
                    className="flex-1 h-10 text-sm border-green-300 text-green-600 hover:bg-green-50 dark:bg-green-900/20"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Excel
                  </Button>
                </div>
              )}
            </div>

            {/* Report Summary Cards */}
            {reportData && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  {(reportTransactionType === 'all' || reportTransactionType === 'income') && (
                    <div className="flex-1 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-md p-1.5 text-center">
                      <p className="text-xs text-green-600">Income</p>
                      <p className="text-sm font-bold text-green-700 truncate">{formatCurrency(reportData.summary.totalIncome)}</p>
                    </div>
                  )}
                  {(reportTransactionType === 'all' || reportTransactionType === 'expense') && (
                    <div className="flex-1 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-md p-1.5 text-center">
                      <p className="text-xs text-red-600">Expense</p>
                      <p className="text-sm font-bold text-red-700 truncate">{formatCurrency(reportData.summary.totalExpenses)}</p>
                    </div>
                  )}
                  {reportTransactionType === 'all' && (
                    <div className={`flex-1 rounded-md p-1.5 text-center ${reportData.summary.netFlow >= 0 ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200' : 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200'}`}>
                      <p className={`text-xs ${reportData.summary.netFlow >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net</p>
                      <p className={`text-sm font-bold truncate ${reportData.summary.netFlow >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{formatCurrency(reportData.summary.netFlow)}</p>
                    </div>
                  )}
                </div>

                {/* Transactions List */}
                {reportData.transactions && reportData.transactions.length > 0 && (
                  <div className="grid grid-cols-1 gap-1.5 max-h-[250px] overflow-y-auto">
                    {reportData.transactions.slice(0, 20).map((t, i) => (
                      <Card key={i} className={`hover:shadow-sm ${t.type === 'income' ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-red-500'}`}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start gap-1">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-800 dark:text-foreground truncate">{t.description}</p>
                              <p className="text-xs text-gray-400 dark:text-muted-foreground">{formatDate(t.date)}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-bold ${t.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                {t.type === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          )}
        </Tabs>
      </Card>

      {/* Account Head Transactions Dialog */}
      <Dialog open={accountTxnDialogOpen} onOpenChange={setAccountTxnDialogOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2 border-b">
            <DialogTitle className="text-sm flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: selectedAccountHead?.color || '#3B82F6' }} />
              {selectedAccountHead?.name || 'Account'}
              <Badge variant="outline" className="text-xs ml-auto">{formatCurrency(selectedAccountHead?.currentBalance || 0)}</Badge>
            </DialogTitle>
            <DialogDescription className="text-sm">All transactions for this account</DialogDescription>
          </DialogHeader>

          {/* Summary Row */}
          {accountTxnSummary && !accountTxnLoading && (
            <div className="grid grid-cols-4 gap-1 px-3 py-2 bg-gray-50 dark:bg-secondary/30 text-center">
              <div>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">Income</p>
                <p className="text-sm font-bold text-green-600">+{formatCurrency(accountTxnSummary.totalIncome)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">Expense</p>
                <p className="text-sm font-bold text-red-600">-{formatCurrency(accountTxnSummary.totalExpense)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">Transfer In</p>
                <p className="text-sm font-bold text-blue-600">+{formatCurrency(accountTxnSummary.totalTransferIn)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">Transfer Out</p>
                <p className="text-sm font-bold text-orange-600">-{formatCurrency(accountTxnSummary.totalTransferOut)}</p>
              </div>
            </div>
          )}

          {/* Filter Row */}
          <div className="flex gap-1 px-3 py-1.5 border-b overflow-x-auto">
            {[
              { key: 'all', label: 'All' },
              { key: 'income', label: 'Income' },
              { key: 'expense', label: 'Expense' },
              { key: 'transfer', label: 'Transfers' }
            ].map(f => (
              <button
                key={f.key}
                data-testid={`txn-filter-${f.key}`}
                onClick={() => setAccountTxnFilter(f.key)}
                className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                  accountTxnFilter === f.key
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {f.label} {f.key === 'all' ? `(${accountTransactions.length})` : ''}
              </button>
            ))}
          </div>

          {/* Transactions List */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
            {accountTxnLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : filteredAccountTransactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400 dark:text-muted-foreground">No transactions found</p>
              </div>
            ) : (
              filteredAccountTransactions.map(txn => (
                <div key={`${txn.type}-${txn.id}`} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-secondary/30 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  {/* Icon */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    txn.type === 'income' ? 'bg-green-100 dark:bg-green-900/30' :
                    txn.type === 'expense' ? 'bg-red-100 dark:bg-red-900/30' :
                    txn.type === 'transfer_in' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    'bg-orange-100 dark:bg-orange-900/30'
                  }`}>
                    {txn.type === 'income' && <TrendingUp className="w-3 h-3 text-green-600" />}
                    {txn.type === 'expense' && <TrendingDown className="w-3 h-3 text-red-600" />}
                    {txn.type === 'transfer_in' && <ArrowDownCircle className="w-3 h-3 text-blue-600" />}
                    {txn.type === 'transfer_out' && <ArrowUpCircle className="w-3 h-3 text-orange-600" />}
                  </div>
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{txn.description || txn.customerName || txn.categoryName || '-'}</p>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-muted-foreground">
                      <span>{formatDate(txn.date)}</span>
                      {txn.paymentMethod && <><span>|</span><span className="capitalize">{txn.paymentMethod.replace('_', ' ')}</span></>}
                      {txn.venueName && <><span>|</span><span className="truncate max-w-[80px]">{txn.venueName}</span></>}
                      {txn.fromAccountName && <><span>|</span><span>From: {txn.fromAccountName}</span></>}
                      {txn.toAccountName && <><span>|</span><span>To: {txn.toAccountName}</span></>}
                    </div>
                  </div>
                  {/* Amount */}
                  <div className="flex-shrink-0 text-right">
                    <p className={`text-sm font-bold ${
                      txn.type === 'income' || txn.type === 'transfer_in' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {txn.type === 'income' || txn.type === 'transfer_in' ? '+' : '-'}{formatCurrency(txn.amount)}
                    </p>
                    <Badge variant="outline" className="text-[11px] px-1 py-0">
                      {txn.type === 'income' ? 'Income' : txn.type === 'expense' ? 'Expense' : txn.type === 'transfer_in' ? 'Txfr In' : 'Txfr Out'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={() => {
          if (deleteType === 'expense') handleDeleteExpense();
          else if (deleteType === 'income') handleDeleteIncome();
          else if (deleteType === 'account') handleDeleteAccount();
        }}
        title={`Delete ${deleteType === 'expense' ? 'Expense' : deleteType === 'income' ? 'Income' : 'Account'}?`}
        description={
          deleteType === 'income' && itemToDelete?.source === 'booking_payment'
            ? 'This income entry is linked to a booking payment. Deleting it will ALSO delete the payment from the booking. Are you sure?'
            : 'Are you sure? This cannot be undone.'
        }
      />
    </div>
  );
};

export default Accounts;
